// Tiny typed wrapper around the control-plane HTTP API for the admin
// portal. Mirrors apps/agent-console/src/api.ts but only exposes
// admin-relevant endpoints.

const apiBase = (import.meta.env.VITE_API_BASE as string | undefined) ?? "";

export type AgentStatus = "available" | "away";
export type AgentRole = "agent" | "admin";

export interface AgentMe {
  agent: {
    id: string;
    email: string;
    name: string | null;
    role: AgentRole;
    status: AgentStatus;
    enabled: boolean;
    quota: { cap: number | null; used: number; overCap: boolean };
  };
  sip: { wsServer: string; user: string; password: string; domain: string; displayName: string };
  iceServers: RTCIceServer[];
}

export interface LoginResponse {
  token: string;
  agent: { id: string; email: string; role: AgentRole; name: string | null };
}

export interface AdminAgent {
  id: string;
  email: string;
  name: string | null;
  sipUser: string;
  role: AgentRole;
  status: AgentStatus;
  enabled: boolean;
  monthlyMinutesCap: number | null;
  monthlyMinutesUsed: number;
  lastSeenAt: string | null;
  createdAt: string;
}

export interface CreateAgentInput {
  email: string;
  name?: string;
  sipUser: string;
  loginPassword?: string;
}
export interface CreateAgentResponse {
  agent: AdminAgent;
  sip: { wsServer: string; user: string; password: string; domain: string; displayName: string };
}

export interface PatchAgentInput {
  name?: string | null;
  enabled?: boolean;
  monthlyMinutesCap?: number | null;
  loginPassword?: string;
}

export type GreetingVoicemailMode = "default" | "silent" | "none";

export interface Greeting {
  id: string;
  name: string;
  voicemailMode: GreetingVoicemailMode;
  durationMs: number | null;
  createdAt: string;
  isActive: boolean;
}

export interface GreetingsList {
  active: string | null;
  greetings: Greeting[];
}

export interface CallFlow {
  id: string;
  name: string;
  graph: FlowGraph;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}
export interface CallFlowSchedule {
  id: string;
  flowId: string;
  weekdayMask: number;
  startsAt: string;
  endsAt: string;
  priority: number;
}

export interface FlowGraph {
  rootId: string;
  nodes: Record<string, FlowNode>;
}
export type FlowNode =
  | { kind: "start"; next: string }
  | { kind: "play"; greetingId: string | null; next: string }
  | {
      kind: "collect";
      greetingId: string | null;
      min: number;
      max: number;
      attachToCallerId: boolean;
      next: string;
    }
  | {
      kind: "menu";
      greetingId: string | null;
      branches: Record<string, string>;
      onTimeout: string;
      onInvalid: string;
      timeoutSec: number;
    }
  | {
      kind: "ring";
      timeoutSec: number;
      onAnswer: string;
      onNoAnswer: string;
      onNoOneOnline: string;
    }
  | { kind: "voicemail"; greetingId: string | null; silent: boolean }
  | { kind: "hangup" };

export interface AuditEntry {
  id: string;
  at: string;
  actorId: string | null;
  actorEmail: string | null;
  action: string;
  payload: Record<string, unknown>;
}

class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit & { auth?: string } = {}): Promise<T> {
  // Only declare a JSON body when one is actually being sent.
  // Fastify rejects DELETE/empty-body requests with content-type: json
  // (FST_ERR_CTP_EMPTY_JSON_BODY).
  const hasBody = init.body != null && init.body !== "";
  const res = await fetch(`${apiBase}${path}`, {
    ...init,
    headers: {
      ...(hasBody ? { "content-type": "application/json" } : {}),
      ...(init.auth ? { authorization: `Bearer ${init.auth}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new ApiError(res.status, `${res.status} ${path}: ${body}`);
  }
  return (await res.json()) as T;
}

export const api = {
  async login(email: string, password: string): Promise<LoginResponse> {
    return request<LoginResponse>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  },
  async me(token: string): Promise<AgentMe> {
    return request<AgentMe>("/api/agents/me", { auth: token });
  },
  async listAgents(token: string): Promise<{ agents: AdminAgent[] }> {
    return request("/api/admin/agents", { auth: token });
  },
  async createAgent(token: string, input: CreateAgentInput): Promise<CreateAgentResponse> {
    return request("/api/admin/agents", {
      method: "POST",
      auth: token,
      body: JSON.stringify(input),
    });
  },
  async patchAgent(token: string, id: string, input: PatchAgentInput): Promise<void> {
    await request(`/api/admin/agents/${encodeURIComponent(id)}`, {
      method: "PATCH",
      auth: token,
      body: JSON.stringify(input),
    });
  },
  async listGreetings(token: string): Promise<GreetingsList> {
    return request<GreetingsList>("/api/greetings", { auth: token });
  },
  async uploadGreeting(
    token: string,
    name: string,
    audio: Blob,
    voicemailMode: GreetingVoicemailMode = "default",
  ): Promise<Greeting> {
    const fd = new FormData();
    fd.append("name", name);
    fd.append("voicemail_mode", voicemailMode);
    fd.append("audio", audio, audio instanceof File ? audio.name : "greeting.webm");
    const res = await fetch(`${apiBase}/api/greetings`, {
      method: "POST",
      headers: { authorization: `Bearer ${token}` },
      body: fd,
    });
    if (!res.ok) throw new ApiError(res.status, `${res.status} upload greeting: ${await res.text()}`);
    return (await res.json()) as Greeting;
  },
  async patchGreetingMode(token: string, id: string, voicemailMode: GreetingVoicemailMode): Promise<void> {
    await request(`/api/greetings/${encodeURIComponent(id)}`, {
      method: "PATCH",
      auth: token,
      body: JSON.stringify({ voicemailMode }),
    });
  },
  async deleteGreeting(token: string, id: string): Promise<void> {
    await request(`/api/greetings/${encodeURIComponent(id)}`, { method: "DELETE", auth: token });
  },
  async setActiveGreeting(token: string, id: string | null): Promise<{ active: string | null }> {
    return request("/api/greetings/active", {
      method: "POST",
      auth: token,
      body: JSON.stringify({ active: id }),
    });
  },
  async fetchGreetingAudio(token: string, id: string): Promise<Blob> {
    const url = `${apiBase}/api/greetings/${encodeURIComponent(id)}/audio`;
    const res = await fetch(url, { headers: { authorization: `Bearer ${token}` } });
    if (!res.ok) throw new ApiError(res.status, `${res.status} fetch greeting audio`);
    return res.blob();
  },
  async getVoicemailGreeting(token: string): Promise<{
    present: boolean;
    durationMs?: number;
    updatedAt?: string;
  }> {
    return request("/api/voicemail/greeting", { auth: token });
  },
  async uploadVoicemailGreeting(token: string, audio: Blob): Promise<void> {
    const fd = new FormData();
    fd.append("audio", audio, "unavail.webm");
    const res = await fetch(`${apiBase}/api/voicemail/greeting`, {
      method: "POST",
      headers: { authorization: `Bearer ${token}` },
      body: fd,
    });
    if (!res.ok) throw new ApiError(res.status, `${res.status} upload vm greeting: ${await res.text()}`);
  },
  async deleteVoicemailGreeting(token: string): Promise<void> {
    await request("/api/voicemail/greeting", { method: "DELETE", auth: token });
  },
  async fetchVoicemailGreetingAudio(token: string): Promise<Blob> {
    const res = await fetch(`${apiBase}/api/voicemail/greeting/audio`, {
      headers: { authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new ApiError(res.status, `${res.status} fetch vm greeting audio`);
    return res.blob();
  },
  async listAudit(token: string, limit = 100): Promise<{ entries: AuditEntry[] }> {
    return request(`/api/audit?limit=${limit}`, { auth: token });
  },

  // ─── Call flows ────────────────────────────────────────────
  async listFlows(token: string): Promise<{ flows: CallFlow[]; schedules: CallFlowSchedule[] }> {
    return request("/api/flows", { auth: token });
  },
  async createFlow(
    token: string,
    input: { name: string; graph: FlowGraph; isDefault?: boolean },
  ): Promise<CallFlow> {
    return request("/api/flows", {
      method: "POST",
      auth: token,
      body: JSON.stringify(input),
    });
  },
  async patchFlow(
    token: string,
    id: string,
    input: { name?: string; graph?: FlowGraph; isDefault?: boolean },
  ): Promise<CallFlow> {
    return request(`/api/flows/${encodeURIComponent(id)}`, {
      method: "PATCH",
      auth: token,
      body: JSON.stringify(input),
    });
  },
  async deleteFlow(token: string, id: string): Promise<void> {
    await request(`/api/flows/${encodeURIComponent(id)}`, {
      method: "DELETE",
      auth: token,
    });
  },
  async createSchedule(
    token: string,
    input: { flowId: string; weekdayMask: number; startsAt: string; endsAt: string; priority?: number },
  ): Promise<CallFlowSchedule> {
    return request("/api/flows/schedules", {
      method: "POST",
      auth: token,
      body: JSON.stringify(input),
    });
  },
  async updateSchedule(
    token: string,
    id: string,
    patch: Partial<{ flowId: string; weekdayMask: number; startsAt: string; endsAt: string; priority: number }>,
  ): Promise<CallFlowSchedule> {
    return request(`/api/flows/schedules/${encodeURIComponent(id)}`, {
      method: "PATCH",
      auth: token,
      body: JSON.stringify(patch),
    });
  },
  async deleteSchedule(token: string, id: string): Promise<void> {
    await request(`/api/flows/schedules/${encodeURIComponent(id)}`, {
      method: "DELETE",
      auth: token,
    });
  },
  async getActiveOverride(token: string): Promise<{ flowId: string | null }> {
    return request("/api/flows/active-override", { auth: token });
  },
  async setActiveOverride(
    token: string,
    flowId: string | null,
  ): Promise<{ flowId: string | null }> {
    return request("/api/flows/active-override", {
      method: "PUT",
      auth: token,
      body: JSON.stringify({ flowId }),
    });
  },
  async getRingCooldown(token: string): Promise<{ seconds: number }> {
    return request("/api/ring-cooldown", { auth: token });
  },
  async setRingCooldown(token: string, seconds: number): Promise<{ seconds: number }> {
    return request("/api/ring-cooldown", {
      method: "PUT",
      auth: token,
      body: JSON.stringify({ seconds }),
    });
  },
};

export { ApiError };
